/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logQnaKBButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { SublistSynonymsCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistSynonymsCell';

type AddSynonymGroupModalProps = BasicModalProps<object> & {
    onSubmitSynonym: (values: string[]) => void;
};

const messages = defineMessages({
    addSynonymGroup: {
        id: 'qnaMaker.qnaMakerAddSynonymGroupModal.addSynonymGroup',
        defaultMessage: 'Add synonym group'
    },
    description: {
        id: 'qnaMaker.qnaMakerAddSynonymGroupModal.description',
        defaultMessage: 'Add a list of words that you would like to have the same meaning within the knowledge base'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerAddSynonymGroupModal.cancel',
        defaultMessage: 'Cancel'
    }
});

/**
 * Represents a modal to add a new synonym group.
 *
 * @param props The properties passed to the modal.
 */
export const QnaMakerAddSynonymGroupModal = withModalMounter((props: AddSynonymGroupModalProps) => {
    const { onSubmitSynonym, onDismiss, ...rest } = props;

    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const [synonyms, setSynonyms] = React.useState([]);

    const handleSubmit = () => {
        logQnaKBButtonClick(serviceName, 'AddSynonymModal', 'Button', 'Add');
        onSubmitSynonym(synonyms);
    };

    const handleDismiss = () => {
        logQnaKBButtonClick(serviceName, 'AddSynonymModal', 'Button', 'Cancel');
        onDismiss();
    };

    return (
        <BasicModal
            {...rest}
            onSubmit={handleSubmit}
            onDismiss={handleDismiss}
            valid={synonyms.length > 0}
            dialogContentProps={{ title: localize(messages.addSynonymGroup), styles: { inner: { overflowY: 'auto' } } }}>
            <Stack verticalFill tokens={{ childrenGap: 20 }}>
                <SublistSynonymsCell
                    isEditMode
                    values={synonyms}
                    onAddValues={values => setSynonyms([...synonyms, ...values])}
                    onRemoveValues={values => setSynonyms(synonyms.filter(v => values.some(value => value !== v)))}
                />
            </Stack>
        </BasicModal>
    );
});
